import { Alert } from 'antd';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import Plot from 'react-plotly.js';
import convertToPlotlyFormat from '../utils/chartConversion';

/**
 * Plotly Chart Renderer Component
 * Renders interactive charts using Plotly.js library.
 * Mirrors Numina's chart rendering approach.
 *
 * @param {Object} spec - Chart specification with encodings
 * @param {Array} data - Chart data array
 * @param {string} chartType - Type of chart to render
 * @param {string} title - Chart title
 */
function PlotlyChartRenderer({ spec = null, data = null, chartType = null, title = null }) {
  const [plotlyData, setPlotlyData] = useState(null);
  const [plotlyLayout, setPlotlyLayout] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!data || data.length === 0) {
      setError('No data available for chart');
      return;
    }

    try {
      const { plotlyData: pData, plotlyLayout: pLayout } = convertToPlotlyFormat(
        spec,
        data,
        chartType,
        title,
      );

      setPlotlyData(pData);
      setPlotlyLayout(pLayout);
      setError(null);
    } catch (err) {
      // Error preparing Plotly chart
      setError(err.message || 'Failed to render chart');
    }
  }, [spec, data, chartType, title]);

  if (error) {
    return <Alert message="Chart Rendering Error" description={error} type="error" showIcon />;
  }

  if (!plotlyData || !plotlyLayout) {
    return (
      <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>Loading chart...</div>
    );
  }

  return (
    <div style={{ width: '100%', minHeight: 400 }}>
      <Plot
        data={plotlyData}
        layout={plotlyLayout}
        config={{ responsive: true, displayModeBar: true }}
        style={{ width: '100%', height: '100%' }}
        useResizeHandler
      />
    </div>
  );
}

PlotlyChartRenderer.propTypes = {
  spec: PropTypes.shape({
    encoding: PropTypes.shape({
      x: PropTypes.shape({
        field: PropTypes.string,
      }),
      y: PropTypes.shape({
        field: PropTypes.string,
      }),
      color: PropTypes.shape({
        field: PropTypes.string,
      }),
    }),
  }),
  data: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  ),
  chartType: PropTypes.string,
  title: PropTypes.string,
};

export default PlotlyChartRenderer;
